// Illustrative streaming-ML effectiveness data for the Elevate demo.
//
// ELEVATE is a brand-new client with no delivery history, so its OWN stage-2
// model output does not exist yet. To show the effectiveness LAYER (not just the
// speed of planning), we display a real snapshot of Tinuiti's patented streaming-
// ML stage-2 diagnostics from a REAL — but deliberately UNNAMED — advertiser: per
// placement, the model's predicted cost per conversion and predicted conversions
// per media dollar. It demonstrates what would optimize ELEVATE's plan once it is
// live and delivering; it is NOT a claim about ELEVATE's results. Only the
// advertiser is anonymized — the placements and model numbers are the real output.
//
// This is exactly the read-only `get_streaming_ml_diagnostics` tool the planning
// agent already consults to ground placement selection; here we make that model
// evidence visible on the demo surface.

export interface PlacementPrediction {
  // Inventory placement (network + package); NOT the advertiser.
  placement: string;
  cpm: number;
  // Stage-2 model predictions.
  predCostPerConversion: number;
  predConversionsPerDollar: number;
}

// A representative sample of the model's per-placement predictions, spanning the
// efficient head to the long tail. Real values; advertiser anonymized. Chosen to
// include the inventory ELEVATE's own plan draws on (OTTA, Tubi, Nexxen, Roku,
// Amazon, Prime, Peacock AX, Disney, EyeQ, Pluto, IMG College).
export const PLACEMENT_PREDICTIONS: PlacementPrediction[] = [
  {
    placement: 'OTTA — World Cup',
    cpm: 6.43,
    predCostPerConversion: 20.22,
    predConversionsPerDollar: 49.46
  },
  {
    placement: 'Amazon — RON',
    cpm: 5.63,
    predCostPerConversion: 21.68,
    predConversionsPerDollar: 46.13
  },
  {
    placement: 'OTTA — RON',
    cpm: 5.08,
    predCostPerConversion: 31.63,
    predConversionsPerDollar: 31.62
  },
  {
    placement: 'OTTA — NFL',
    cpm: 6.13,
    predCostPerConversion: 35.12,
    predConversionsPerDollar: 28.48
  },
  {
    placement: 'Nexxen — Tennis',
    cpm: 4.67,
    predCostPerConversion: 39.13,
    predConversionsPerDollar: 25.56
  },
  {
    placement: 'Nexxen — MLB',
    cpm: 4.64,
    predCostPerConversion: 44.26,
    predConversionsPerDollar: 22.59
  },
  {
    placement: 'Nexxen — RON',
    cpm: 6.51,
    predCostPerConversion: 69.04,
    predConversionsPerDollar: 14.48
  },
  {
    placement: 'EyeQ — Video SF',
    cpm: 5.98,
    predCostPerConversion: 69.61,
    predConversionsPerDollar: 14.37
  },
  {
    placement: 'Tubi — Lite',
    cpm: 4.99,
    predCostPerConversion: 70.37,
    predConversionsPerDollar: 14.21
  },
  {
    placement: 'Pluto TV — RON',
    cpm: 5.44,
    predCostPerConversion: 97.98,
    predConversionsPerDollar: 10.21
  },
  {
    placement: 'IMG College — RON',
    cpm: 5.43,
    predCostPerConversion: 118.98,
    predConversionsPerDollar: 8.41
  },
  {
    placement: 'Tubi — RON',
    cpm: 6.75,
    predCostPerConversion: 126.16,
    predConversionsPerDollar: 7.93
  },
  {
    placement: 'Roku — Sports',
    cpm: 8.99,
    predCostPerConversion: 166.93,
    predConversionsPerDollar: 5.99
  },
  {
    placement: 'Peacock AX — RON',
    cpm: 10.32,
    predCostPerConversion: 197.3,
    predConversionsPerDollar: 5.07
  },
  {
    placement: 'Disney Ent. — Hulu RON',
    cpm: 12.45,
    predCostPerConversion: 249.69,
    predConversionsPerDollar: 4.01
  },
  {
    placement: 'Disney+ — RON',
    cpm: 12.9,
    predCostPerConversion: 371.29,
    predConversionsPerDollar: 2.69
  },
  {
    placement: 'Prime Video — RON',
    cpm: 19.53,
    predCostPerConversion: 423.29,
    predConversionsPerDollar: 2.36
  }
];

export interface EffectivenessStats {
  best: number;
  median: number;
  worst: number;
  count: number;
  // How many times more cost-efficient the best placement is vs the least efficient.
  efficiencySpread: number;
}

const median = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
};

// Summary stats over predicted cost-per-conversion — the effectiveness lever:
// the plan concentrates budget where conversions are cheapest, not just where
// reach is cheapest.
export const effectivenessStats = (
  rows: PlacementPrediction[] = PLACEMENT_PREDICTIONS
): EffectivenessStats => {
  const cpas = rows.map(r => r.predCostPerConversion);
  const best = Math.min(...cpas);
  const worst = Math.max(...cpas);
  return {
    best,
    median: median(cpas),
    worst,
    count: rows.length,
    efficiencySpread: best > 0 ? worst / best : 0
  };
};

// Ranked most-efficient first (highest predicted conversions per dollar).
export const rankedByEfficiency = (
  rows: PlacementPrediction[] = PLACEMENT_PREDICTIONS
): PlacementPrediction[] =>
  [...rows].sort((a, b) => b.predConversionsPerDollar - a.predConversionsPerDollar);
